// src/layouts/Sidebar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useTheme,
  useMediaQuery,
  Box,
  alpha,
  IconButton,
  ListSubheader,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

function Sidebar({ navCategories, mobileOpen, handleDrawerToggle, drawerWidth }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isDark = theme.palette.mode === 'dark';
  const location = useLocation();

  const [pinnedPaths, setPinnedPaths] = useState(() => {
    const saved = localStorage.getItem('umbrella_pinned_tools');
    return saved ? JSON.parse(saved) : [];
  });

  const togglePin = (e, path) => {
    e.preventDefault();
    e.stopPropagation();
    setPinnedPaths(prev => {
      const newPins = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      localStorage.setItem('umbrella_pinned_tools', JSON.stringify(newPins));
      return newPins;
    });
  };

  const allLinks = navCategories?.flatMap(cat => cat.links) || [];
  const pinnedLinks = allLinks.filter(link => pinnedPaths.includes(link.path));

  const displayCategories = pinnedLinks.length > 0 
    ? [{ title: 'Pinned Tools', links: pinnedLinks }, ...(navCategories || [])]
    : navCategories || [];

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'text.secondary',
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '16px 8px !important', minHeight: '80px !important' }}>
        <Link to="/home" onClick={handleDrawerToggle} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img
            src="/logo-full-s.png"
            alt="Logo"
            style={{
              height: '48px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: isDark ? 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.3))' : 'none'
            }}
          />
        </Link>
      </Toolbar>
      <Box sx={{
        px: 2,
        pb: 2,
        flexGrow: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        <List sx={{ pt: 0 }}>
          {displayCategories?.map((category, catIndex) => (
            <React.Fragment key={category.title}>
              <ListSubheader 
                sx={{ 
                  bgcolor: 'transparent', 
                  color: 'text.secondary', 
                  fontWeight: 700, 
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase', 
                  lineHeight: '32px',
                  mt: catIndex > 0 ? 1 : 0 
                }}
              >
                {category.title}
              </ListSubheader>
              {category.links.map((link) => {
                // Check if the current path matches the link path (or starts with it for nested routes)
                const isActive = location.pathname === link.path || (link.path !== '/' && link.path !== '/home' && location.pathname.startsWith(link.path));

                return (
                  <ListItem key={`${category.title}-${link.path}`} disablePadding sx={{ mb: 0.5, display: 'block', position: 'relative', '&:hover .pin-btn': { opacity: 1 } }}>
                    <ListItemButton
                      component={Link}
                      to={link.path}
                      onClick={handleDrawerToggle}
                      sx={{
                        minHeight: 48,
                        justifyContent: 'initial',
                        px: 2.5,
                        borderRadius: 3,
                        transition: 'all 0.2s ease-in-out',
                        position: 'relative',
                        overflow: 'hidden',
                        ...(isActive ? {
                          backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
                          color: theme.palette.primary.main,
                          boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '10%',
                            height: '80%',
                            width: '4px',
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '0 4px 4px 0',
                          }
                        } : {
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.text.primary, 0.05),
                            transform: 'translateX(4px)',
                            color: 'text.primary',
                          },
                        }),
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          mr: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          color: isActive ? theme.palette.primary.main : 'inherit',
                          transition: 'all 0.2s ease-in-out',
                          ...(isActive && {
                            filter: `drop-shadow(0 0 8px ${alpha(theme.palette.primary.main, 0.5)})`,
                          })
                        }}
                      >
                        {link.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{
                          fontSize: '0.95rem',
                          fontWeight: isActive ? 700 : 500,
                          transition: 'font-weight 0.2s'
                        }}
                      />
                    </ListItemButton>
                    <IconButton
                      className="pin-btn"
                      size="small"
                      onClick={(e) => togglePin(e, link.path)}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: pinnedPaths.includes(link.path) ? 1 : 0,
                        transition: 'opacity 0.2s',
                        color: pinnedPaths.includes(link.path) ? theme.palette.primary.main : 'text.secondary',
                        bgcolor: isDark ? alpha('#0f172a', 0.8) : alpha('#ffffff', 0.8),
                        '&:hover': {
                          bgcolor: isDark ? alpha('#0f172a', 0.9) : alpha('#ffffff', 0.9),
                        }
                      }}
                    >
                      {pinnedPaths.includes(link.path) ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </ListItem>
                );
              })}
              {catIndex < navCategories.length - 1 && <Divider sx={{ my: 1, mx: 2, opacity: 0.5 }} />}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ flexShrink: 0 }}
      aria-label="mailbox folders"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: isDark ? alpha('#0f172a', 0.98) : alpha('#ffffff', 0.98),
            backdropFilter: 'blur(20px)',
            borderRight: `1px solid ${isDark ? alpha('#fff', 0.05) : alpha(theme.palette.divider, 0.8)}`,
            transition: 'all 0.3s ease',
            overflowX: 'hidden',
            overflowY: 'hidden',
            boxShadow: `12px 0 32px ${alpha('#000', 0.3)}`,
            zIndex: 1400,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}

export default Sidebar;
